import { useCallback, useEffect, useRef, useState } from 'react';
import {
  COMMUNICATIONS_CHANGE_EVENT,
  getCommunicationsMessages,
  getMutedThreads,
  loadProviderStatus,
  refreshInboxFromApi,
} from '../../services/CommunicationsService';
import type { InboxMessage, ProviderStatus } from '../../types/Communications';

export const POLL_INTERVAL_MS = 30_000;

export interface CommunicationsData {
  messages: InboxMessage[];
  mutedThreads: string[];
  provider: ProviderStatus | null;
  loading: boolean;
  /** The last load/poll failure, or null. The previous messages stay on screen. */
  loadError: unknown;
  refresh: () => Promise<void>;
  reloadProvider: () => Promise<void>;
}

/**
 * Loads the inbox and the provider status for the active project, mirrors the
 * service cache, polls every 30s, and starts over when the project changes.
 */
export function useCommunicationsData(projectId: string | null): CommunicationsData {
  const [messages, setMessages] = useState<InboxMessage[]>(getCommunicationsMessages);
  const [mutedThreads, setMutedThreads] = useState<string[]>(getMutedThreads);
  const [provider, setProvider] = useState<ProviderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    const sync = () => {
      setMessages(getCommunicationsMessages());
      setMutedThreads(getMutedThreads());
    };
    window.addEventListener(COMMUNICATIONS_CHANGE_EVENT, sync);
    return () => window.removeEventListener(COMMUNICATIONS_CHANGE_EVENT, sync);
  }, []);

  const refresh = useCallback(async () => {
    try {
      await refreshInboxFromApi();
      if (alive.current) setLoadError(null);
    } catch (err) {
      if (alive.current) setLoadError(err);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  const reloadProvider = useCallback(async () => {
    try {
      const status = await loadProviderStatus();
      if (alive.current) setProvider(status);
    } catch {
      // Unknown status: leave the last value. A send is still checked by the server (409).
    }
  }, []);

  useEffect(() => {
    // New project: drop the old project's data and load this one's.
    setMessages(getCommunicationsMessages());
    setMutedThreads(getMutedThreads());
    setProvider(null);
    setLoading(true);
    void refresh();
    void reloadProvider();
    const id = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [projectId, refresh, reloadProvider]);

  return { messages, mutedThreads, provider, loading, loadError, refresh, reloadProvider };
}
